import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { testOrderStatusToBadgeVariant } from '@/lib/clinicalBadges';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { TestOrderViewModel } from '@/features/patient-record/types';

import { TestOrderExpanded } from './TestOrderExpanded';

interface TestOrderRowProps {
  readonly guid: string;
  readonly order: TestOrderViewModel;
  readonly onEdit: () => void;
  readonly onCancel: () => void;
}

export function TestOrderRow({
  guid,
  order,
  onEdit,
  onCancel,
}: TestOrderRowProps): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const displayName = order.panelName ?? order.testName;
  // Unreviewed results get a left accent border per the Phase 1 spec, so
  // RESULTED orders stand out in a long list.
  const accentResulted = order.status === 'RESULTED';

  return (
    <div
      className={cn(
        'group rounded-md -mx-2 px-2 py-1.5 transition-colors',
        accentResulted && 'border-l-2 border-primary/70 pl-3',
      )}
    >
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        className="w-full flex items-start gap-2 text-left min-w-0"
      >
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'h-4 w-4 mt-0.5 text-muted-foreground transition-transform shrink-0',
            open && 'rotate-180',
          )}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-card-foreground truncate">
                {displayName}
              </p>
              {order.labProvider !== null && (
                <p className="text-xs text-muted-foreground truncate">
                  {order.labProvider}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge
                variant={testOrderStatusToBadgeVariant(order.status)}
                className="text-xs h-5"
              >
                {t(`patient.testOrders.status.${order.status}`)}
              </Badge>
              <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                {formatDate(order.orderedDate, { month: 'short', day: 'numeric' })}
              </span>
            </div>
          </div>
        </div>
      </button>
      {open && (
        <TestOrderExpanded
          guid={guid}
          order={order}
          onEdit={onEdit}
          onCancel={onCancel}
        />
      )}
    </div>
  );
}
